import { Link } from "react-router-dom";
import PublicFooter from "@/components/PublicFooter";

const LOGO = "/brand/mtg_logo.svg";

function Section({ title, children }) {
  return (
    <section className="mt-8">
      <h2 className="font-display text-xl font-bold text-[#16324F]">{title}</h2>
      <div className="mt-2 text-sm text-slate-600 leading-relaxed space-y-1.5">{children}</div>
    </section>
  );
}

export default function Confidentialite() {
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid="page-confidentialite">
      <div className="px-6 md:px-12 py-6 max-w-3xl mx-auto w-full">
        <Link to="/" className="inline-flex items-center gap-2" data-testid="privacy-home-link">
          <img src={LOGO} alt="MonTempsGagné" className="h-9" />
        </Link>
        <Link to="/" className="mt-6 inline-block text-sm text-slate-500 hover:text-[#16324F]">← Retour à l'accueil</Link>

        <h1 className="mt-4 font-display text-3xl md:text-4xl font-extrabold text-[#16324F] tracking-tight">Politique de confidentialité</h1>
        <p className="mt-3 text-sm text-slate-500">
          Cette page explique quelles données sont traitées par le service MonTempsGagné, dans quels buts et
          quels sont vos droits.
        </p>

        <Section title="Deux rôles distincts">
          <p>
            <strong>FlamingOps</strong> (Kevin Fernandez) est responsable de traitement pour les données de ses
            propres prospects (formulaire professionnel de ce site) et pour la gestion des comptes professionnels.
          </p>
          <p>
            Lorsqu'une entreprise cliente utilise MonTempsGagné pour recevoir les demandes de <em>ses</em> clients
            (devis, réservations), c'est cette entreprise qui est responsable de traitement ; FlamingOps agit alors
            comme sous-traitant qui héberge et opère l'outil pour son compte.
          </p>
        </Section>

        <Section title="Données traitées et finalités">
          <p><strong>1. Formulaire professionnel (prospects FlamingOps)</strong> — nom, e-mail, entreprise, métier, besoins, mode de tarification, site internet et précisions. But : répondre à votre demande et préparer une proposition. Base légale : mesures précontractuelles prises à votre demande et intérêt légitime.</p>
          <p><strong>2. Demandes de devis</strong> — informations sur le véhicule, la prestation, la localisation et vos coordonnées (nom, téléphone, e-mail, adresse). But : calculer et transmettre le devis au professionnel concerné. Base légale : mesures précontractuelles / exécution.</p>
          <p><strong>3. Réservations</strong> — créneau choisi et coordonnées. But : organiser l'intervention avec le professionnel. Base légale : exécution de la demande.</p>
          <p><strong>4. Comptes professionnels</strong> — e-mail, mot de passe (stocké sous forme chiffrée/hachée) et informations d'entreprise. But : accès sécurisé à l'espace professionnel. Base légale : exécution du contrat de service.</p>
          <p><strong>5. E-mails transactionnels</strong> — confirmations de devis, de réservation et accusés de réception. But : informer les parties. Base légale : exécution / intérêt légitime.</p>
        </Section>

        <Section title="Destinataires et sous-traitants">
          <p>Les données sont accessibles à FlamingOps et, pour les devis et réservations, au professionnel destinataire de la demande.</p>
          <p>Prestataires techniques utilisés :</p>
          <p>• <strong>Emergent</strong> (Agione Technologies Private Limited) : hébergement et exploitation de l'application.</p>
          <p>• <strong>Resend</strong> : envoi des e-mails transactionnels.</p>
          <p>• Base de données <strong>MongoDB</strong> pour le stockage applicatif.</p>
        </Section>

        <Section title="Durées de conservation">
          <p>Prospects : conservés le temps de traiter la demande, puis jusqu'à 3 ans après le dernier contact.</p>
          <p>Devis et réservations : conservés le temps de la relation avec le professionnel, puis archivés selon les obligations applicables (par exemple facturation).</p>
          <p>Comptes professionnels : conservés pendant la durée d'utilisation du compte.</p>
          <p>À l'issue de ces durées, les données sont supprimées ou anonymisées.</p>
        </Section>

        <Section title="Transferts hors Union européenne">
          <p>
            Certains prestataires (plateforme d'hébergement, service d'e-mail) peuvent être situés hors de l'Union
            européenne. Le cas échéant, ces transferts sont encadrés par les garanties prévues par la réglementation
            applicable. La localisation précise des données dépend des sous-traitants d'hébergement (voir les mentions
            légales et la page sous-traitants d'Emergent).
          </p>
        </Section>

        <Section title="Cookies et traceurs">
          <p>
            MonTempsGagné n'utilise <strong>pas</strong> de cookies publicitaires ni d'outil de mesure d'audience.
          </p>
          <p>
            Seuls des éléments strictement nécessaires au fonctionnement sont utilisés : un cookie de session sécurisé
            (<span className="font-mono">access_token</span>) et un stockage local du navigateur
            (<span className="font-mono">kf_token</span>) permettant de maintenir la connexion à l'espace professionnel.
            N'étant pas soumis au consentement, aucun bandeau n'est requis.
          </p>
        </Section>

        <Section title="Vos droits">
          <p>
            Vous disposez d'un droit d'accès, de rectification, d'effacement, de limitation, d'opposition et de
            portabilité. Pour les demandes de devis et réservations, adressez-vous en priorité au professionnel
            concerné ; pour les autres traitements, contactez FlamingOps.
          </p>
          <p>Vous pouvez introduire une réclamation auprès de la CNIL (www.cnil.fr).</p>
        </Section>

        <Section title="Contact">
          <p>FlamingOps — <a href="mailto:fernandez@flamingops.fr" className="text-[#19C3B3] underline underline-offset-2">fernandez@flamingops.fr</a>.</p>
          <p>Voir aussi les <Link to="/mentions-legales" className="text-[#19C3B3] underline underline-offset-2">mentions légales</Link>.</p>
        </Section>
      </div>
      <div className="mt-12"><PublicFooter /></div>
    </div>
  );
}
